import {
  ALL_APP_LOCALES,
  DEFAULT_LOCALE,
  type AppLocale,
} from "@/lib/i18n/constants";
import type { ErrorPageAction, ErrorPageContent } from "./error-pages";

/**
 * Minimal, client-safe English fallback for the root error boundary.
 *
 * Keep this payload dependency-free: importing the full content resolver from
 * `app/global-error.tsx` pulls every locale into the shared client bundle.
 * `error-pages.ts` consumes the same base payload for the registry entry so
 * this remains the single source of truth for the fallback copy.
 */
export const RUNTIME_ERROR_BASE_CONTENT = {
  statusCode: 500,
  eyebrow: "500",
  title: "Something did not load correctly.",
  lead: "The page hit a temporary error. You can try again or continue from a stable SEOH route.",
  metadata: {
    title: "Error loading page | SEOH",
    description:
      "SEOH could not load this page. Try again or continue from a stable route.",
  },
  primaryAction: {
    label: "Try again",
    href: "#retry",
    description: "Ask the page to reload this section.",
  },
  secondaryAction: {
    label: "Go to homepage",
    href: "/",
    description: "Return to the main SEOH entry point.",
  },
  suggestionsEyebrow: "Keep going",
  suggestionsTitle: "Use a stable route while this is checked.",
  suggestions: [
    {
      label: "Homepage",
      href: "/",
      description: "Start again from the main SEOH page.",
    },
    {
      label: "Services",
      href: "/services",
      description: "Browse the core service pages.",
    },
    {
      label: "Contact",
      href: "/contact",
      description: "Send the context if the issue keeps happening.",
    },
  ],
  retryLabel: "Try again",
  digestLabel: "Reference",
  statsAria: "Error page status",
  statusLabel: "Status",
  indexingLabel: "Indexing",
  noindexValue: "Noindex",
  ampLabel: "AMP",
  ampNotAdvertisedValue: "Not advertised",
} as const satisfies ErrorPageContent;

function localizeEnglishAction(action: ErrorPageAction): ErrorPageAction {
  if (
    action.href.startsWith("#") ||
    action.href.startsWith("http://") ||
    action.href.startsWith("https://")
  ) {
    return action;
  }

  return {
    ...action,
    href: action.href === "/" ? "/en" : `/en${action.href}`,
  };
}

export const GLOBAL_RUNTIME_ERROR_FALLBACK: ErrorPageContent = {
  ...RUNTIME_ERROR_BASE_CONTENT,
  primaryAction: localizeEnglishAction(
    RUNTIME_ERROR_BASE_CONTENT.primaryAction,
  ),
  secondaryAction: localizeEnglishAction(
    RUNTIME_ERROR_BASE_CONTENT.secondaryAction,
  ),
  suggestions: RUNTIME_ERROR_BASE_CONTENT.suggestions.map(
    localizeEnglishAction,
  ),
};

export const GLOBAL_RUNTIME_ERROR_SKIP_LABEL = "Skip to main content";

type MinimalRuntimeErrorCopy = {
  readonly title: string;
  readonly lead: string;
  readonly metadata: {
    readonly title: string;
    readonly description: string;
  };
  readonly primaryAction: Omit<ErrorPageAction, "href">;
  readonly secondaryAction: Omit<ErrorPageAction, "href">;
  readonly suggestionsEyebrow: string;
  readonly suggestionsTitle: string;
  readonly suggestions: readonly [
    Omit<ErrorPageAction, "href">,
    Omit<ErrorPageAction, "href">,
    Omit<ErrorPageAction, "href">,
  ];
  readonly retryLabel: string;
  readonly digestLabel: string;
  readonly statsAria: string;
  readonly statusLabel: string;
  readonly indexingLabel: string;
  readonly noindexValue: string;
  readonly ampNotAdvertisedValue: string;
  readonly skipLabel: string;
};

/*
 * This is deliberately a small, locale-aware registry for the error
 * boundary's synchronous state. The complete 500-page content remains in
 * error-pages.ts and is loaded after hydration; copying its full dictionaries
 * here would put every locale into the initial error-boundary bundle.
 */
const MINIMAL_RUNTIME_ERROR_COPY: Record<
  Exclude<AppLocale, "en">,
  MinimalRuntimeErrorCopy
> = {
  ru: {
    title: "Что-то не загрузилось корректно.",
    lead: "Страница столкнулась с временной ошибкой. Попробуйте снова или вернитесь по стабильному маршруту SEOH.",
    metadata: {
      title: "Ошибка загрузки страницы | SEOH",
      description:
        "SEOH не смог загрузить эту страницу. Попробуйте снова или перейдите по стабильному маршруту.",
    },
    primaryAction: {
      label: "Повторить",
      description: "Попросите страницу перезагрузить этот раздел.",
    },
    secondaryAction: {
      label: "Перейти на главную",
      description: "Вернуться в основную точку входа SEOH.",
    },
    suggestionsEyebrow: "Продолжить",
    suggestionsTitle:
      "Воспользуйтесь стабильным маршрутом, пока это проверяется.",
    suggestions: [
      {
        label: "Главная",
        description: "Начните заново с главной страницы SEOH.",
      },
      {
        label: "Услуги",
        description: "Ознакомьтесь с основными страницами услуг.",
      },
      {
        label: "Контакты",
        description: "Отправьте информацию, если проблема повторяется.",
      },
    ],
    retryLabel: "Повторить",
    digestLabel: "Справка",
    statsAria: "Статус страницы с ошибкой",
    statusLabel: "Статус",
    indexingLabel: "Индексация",
    noindexValue: "Не индексируется",
    ampNotAdvertisedValue: "Не рекламируется",
    skipLabel: "Перейти к основному содержимому",
  },
  ar: {
    title: "حدث تحميل غير صحيح.",
    lead: "تعرضت الصفحة لخطأ مؤقت. يمكنك المحاولة مرة أخرى أو المتابعة من مسار ثابت في SEOH.",
    metadata: {
      title: "خطأ في تحميل الصفحة | SEOH",
      description:
        "تعذر على SEOH تحميل هذه الصفحة. جرّب مرة أخرى أو تابع من مسار ثابت.",
    },
    primaryAction: {
      label: "حاول مرة أخرى",
      description: "اطلب من الصفحة إعادة تحميل هذا القسم.",
    },
    secondaryAction: {
      label: "الذهاب إلى الصفحة الرئيسية",
      description: "العودة إلى نقطة الدخول الرئيسية في SEOH.",
    },
    suggestionsEyebrow: "تابع",
    suggestionsTitle: "استخدم مسارًا ثابتًا أثناء التحقق من هذا الأمر.",
    suggestions: [
      {
        label: "الصفحة الرئيسية",
        description: "ابدأ مرة أخرى من الصفحة الرئيسية لـ SEOH.",
      },
      { label: "الخدمات", description: "تصفح صفحات الخدمات الأساسية." },
      { label: "اتصل", description: "أرسل السياق إذا تكرر المشكلة." },
    ],
    retryLabel: "حاول مرة أخرى",
    digestLabel: "مرجع",
    statsAria: "حالة صفحة الخطأ",
    statusLabel: "الحالة",
    indexingLabel: "الفهرسة",
    noindexValue: "عدم الفهرسة",
    ampNotAdvertisedValue: "غير مُعلن عنه",
    skipLabel: "تخطي إلى المحتوى الرئيسي",
  },
  es: {
    title: "Algo no se cargó correctamente.",
    lead: "La página tuvo un error temporal. Puedes intentarlo de nuevo o continuar desde una ruta estable de SEOH.",
    metadata: {
      title: "Error al cargar la página | SEOH",
      description:
        "SEOH no pudo cargar esta página. Intenta de nuevo o continúa desde una ruta estable.",
    },
    primaryAction: {
      label: "Intentar de nuevo",
      description: "Pide que la página recargue esta sección.",
    },
    secondaryAction: {
      label: "Ir a la página principal",
      description: "Volver al punto de entrada principal de SEOH.",
    },
    suggestionsEyebrow: "Sigue adelante",
    suggestionsTitle: "Usa una ruta estable mientras se revisa esto.",
    suggestions: [
      {
        label: "Página principal",
        description: "Comienza de nuevo desde la página principal de SEOH.",
      },
      {
        label: "Servicios",
        description: "Navega por las páginas de servicios principales.",
      },
      {
        label: "Contacto",
        description: "Envía el contexto si el problema persiste.",
      },
    ],
    retryLabel: "Intentar de nuevo",
    digestLabel: "Referencia",
    statsAria: "Estado de la página de error",
    statusLabel: "Estado",
    indexingLabel: "Indexación",
    noindexValue: "Sin indexar",
    ampNotAdvertisedValue: "No anunciado",
    skipLabel: "Saltar al contenido principal",
  },
  fr: {
    title: "Chargement incorrect d’un élément de la page.",
    lead: "La page a rencontré une erreur temporaire. Vous pouvez réessayer ou poursuivre depuis une route stable de SEOH.",
    metadata: {
      title: "Erreur de chargement | SEOH",
      description:
        "SEOH n’a pas pu charger cette page. Réessayez ou suivez une route stable.",
    },
    primaryAction: {
      label: "Réessayer",
      description: "Demander au site de recharger cette section.",
    },
    secondaryAction: {
      label: "Aller à l’accueil",
      description: "Retourner au point d’entrée principal de SEOH.",
    },
    suggestionsEyebrow: "Poursuivre",
    suggestionsTitle: "Utilisez une route stable pendant la vérification.",
    suggestions: [
      {
        label: "Page d’accueil",
        description: "Recommencez depuis la page principale de SEOH.",
      },
      {
        label: "Nos services",
        description: "Parcourez les pages de services principales.",
      },
      {
        label: "Contactez‑nous",
        description: "Envoyez le contexte si le problème persiste.",
      },
    ],
    retryLabel: "Réessayer",
    digestLabel: "Référence",
    statsAria: "Statut de la page d'erreur",
    statusLabel: "Statut",
    indexingLabel: "Indexation",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Non annoncé",
    skipLabel: "Aller au contenu principal",
  },
  he: {
    title: "משהו לא נטען כראוי.",
    lead: "העמוד נתקל בשגיאה זמנית. ניתן לנסות שוב או להמשיך דרך נתיב יציב ב‑SEOH.",
    metadata: {
      title: "שגיאה בטעינת הדף | SEOH",
      description: "SEOH לא יכול לטעון דף זה. נסו שוב או המשיכו דרך נתיב יציב.",
    },
    primaryAction: {
      label: "נסה שוב",
      description: "בקשו מהעמוד לטעון מחדש חלק זה.",
    },
    secondaryAction: {
      label: "עבור לדף הבית",
      description: "חזרו לנקודת הכניסה הראשית של SEOH.",
    },
    suggestionsEyebrow: "המשיכו",
    suggestionsTitle: "השתמשו בנתיב יציב בזמן שבודקים זאת.",
    suggestions: [
      { label: "דף הבית", description: "התחילו שוב מדף ה‑SEOH הראשי." },
      { label: "שירותים", description: "עיינו בעמודי השירות המרכזיים." },
      { label: "צור קשר", description: "שלחו את ההקשר אם הבעיה חוזרת." },
    ],
    retryLabel: "נסה שוב",
    digestLabel: "הפניה",
    statsAria: "מצב דף שגיאה",
    statusLabel: "סטטוס",
    indexingLabel: "אינדוקס",
    noindexValue: "לא מאונדקס",
    ampNotAdvertisedValue: "לא מוצג",
    skipLabel: "דלג לתוכן הראשי",
  },
  de: {
    title: "Beim Laden ist etwas schiefgelaufen.",
    lead: "Die Seite hat einen temporären Fehler verursacht. Versuchen Sie es erneut oder kehren Sie über eine stabile SEOH-Route zurück.",
    metadata: {
      title: "Fehler beim Laden der Seite | SEOH",
      description:
        "SEOH konnte diese Seite nicht laden. Versuchen Sie es erneut oder wechseln Sie zu einer stabilen Route.",
    },
    primaryAction: {
      label: "Erneut versuchen",
      description: "Die Seite anweisen, diesen Abschnitt neu zu laden.",
    },
    secondaryAction: {
      label: "Zur Startseite",
      description: "Zurück zum Hauptzugang von SEOH.",
    },
    suggestionsEyebrow: "Weitermachen",
    suggestionsTitle:
      "Nutzen Sie eine stabile Route, während dies überprüft wird.",
    suggestions: [
      {
        label: "Startseite",
        description: "Nochmals von der Hauptseite von SEOH beginnen.",
      },
      {
        label: "Leistungen",
        description: "Durchsuchen Sie die Kernleistungsseiten.",
      },
      {
        label: "Kontakt",
        description:
          "Senden Sie den Kontext, falls das Problem weiterhin auftritt.",
      },
    ],
    retryLabel: "Erneut versuchen",
    digestLabel: "Referenz",
    statsAria: "Fehlerseitenstatus",
    statusLabel: "Status",
    indexingLabel: "Indexierung",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Nicht beworben",
    skipLabel: "Zum Hauptinhalt springen",
  },
  "pt-br": {
    title: "Algo não carregou corretamente.",
    lead: "A página encontrou um erro temporário. Tente novamente ou siga por uma rota estável do SEOH.",
    metadata: {
      title: "Erro ao carregar a página | SEOH",
      description:
        "O SEOH não conseguiu carregar esta página. Tente novamente ou navegue por uma rota estável.",
    },
    primaryAction: {
      label: "Tentar novamente",
      description: "Recarregar esta seção da página.",
    },
    secondaryAction: {
      label: "Ir para a página inicial",
      description: "Voltar ao ponto de entrada principal do SEOH.",
    },
    suggestionsEyebrow: "Continuar",
    suggestionsTitle: "Use uma rota estável enquanto verificamos isso.",
    suggestions: [
      {
        label: "Página inicial",
        description: "Comece novamente a partir da página principal do SEOH.",
      },
      {
        label: "Serviços",
        description: "Explore nossas páginas principais de serviços.",
      },
      {
        label: "Contato",
        description: "Envie o contexto caso o problema persista.",
      },
    ],
    retryLabel: "Tentar novamente",
    digestLabel: "Referência",
    statsAria: "Status da página de erro",
    statusLabel: "Situação",
    indexingLabel: "Indexação",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Não divulgado",
    skipLabel: "Ir para o conteúdo principal",
  },
  ja: {
    title: "一部が正しく読み込まれませんでした。",
    lead: "ページで一時的なエラーが発生しました。再試行するか、安定したSEOHルートから続けてください。",
    metadata: {
      title: "ページ読み込みエラー | SEOH",
      description:
        "SEOHでこのページを読み込めませんでした。再試行するか、安定したルートから続けてください。",
    },
    primaryAction: {
      label: "再試行する",
      description: "このセクションをリロードします。",
    },
    secondaryAction: {
      label: "ホームに戻る",
      description: "SEOHのメイン入口に戻ります。",
    },
    suggestionsEyebrow: "先に進む",
    suggestionsTitle: "チェック中は安定したルートをご利用ください。",
    suggestions: [
      {
        label: "ホームページ",
        description: "SEOHのメインページからやり直してください。",
      },
      {
        label: "サービス",
        description: "主要なサービスページをご覧ください。",
      },
      {
        label: "お問い合わせ",
        description: "問題が続く場合は状況をお送りください。",
      },
    ],
    retryLabel: "再試行",
    digestLabel: "参照",
    statsAria: "エラーページのステータス",
    statusLabel: "ステータス",
    indexingLabel: "インデックス",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "広告は掲載されていません",
    skipLabel: "メインコンテンツへスキップ",
  },
  hi: {
    title: "कुछ सही तरीके से लोड नहीं हुआ।",
    lead: "पृष्ठ को अस्थायी त्रुटि का सामना करना पड़ा। आप पुनः प्रयास कर सकते हैं या किसी स्थिर SEOH मार्ग से जारी रख सकते हैं।",
    metadata: {
      title: "पृष्ठ लोड करते समय त्रुटि | SEOH",
      description:
        "SEOH यह पृष्ठ लोड नहीं कर सका। पुनः प्रयास करें या किसी स्थिर रूट से जारी रखें।",
    },
    primaryAction: {
      label: "पुनः प्रयास करें",
      description: "इस अनुभाग को फिर से लोड करने के लिए पृष्ठ से कहें।",
    },
    secondaryAction: {
      label: "होमपेज पर जाएँ",
      description: "मुख्य SEOH प्रवेश बिंदु पर लौटें।",
    },
    suggestionsEyebrow: "जारी रखें",
    suggestionsTitle: "जांच के दौरान किसी स्थिर मार्ग का उपयोग करें।",
    suggestions: [
      { label: "होमपेज", description: "मुख्य SEOH पृष्ठ से फिर शुरू करें।" },
      { label: "सेवाएँ", description: "मुख्य सेवा पृष्ठ ब्राउज़ करें।" },
      { label: "संपर्क", description: "यदि समस्या जारी रहे तो संदर्भ भेजें।" },
    ],
    retryLabel: "पुनः प्रयास करें",
    digestLabel: "संदर्भ",
    statsAria: "त्रुटि पृष्ठ स्थिति",
    statusLabel: "स्थिति",
    indexingLabel: "इंडेक्सिंग",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "विज्ञापित नहीं",
    skipLabel: "मुख्य सामग्री पर जाएँ",
  },
  id: {
    title: "Ada yang tidak dimuat dengan benar.",
    lead: "Halaman mengalami kesalahan sementara. Anda dapat mencoba lagi atau melanjutkan dari rute SEOH yang stabil.",
    metadata: {
      title: "Kesalahan memuat halaman | SEOH",
      description:
        "SEOH tidak dapat memuat halaman ini. Coba lagi atau lanjutkan dari rute yang stabil.",
    },
    primaryAction: {
      label: "Coba lagi",
      description: "Minta halaman untuk memuat ulang bagian ini.",
    },
    secondaryAction: {
      label: "Pergi ke beranda",
      description: "Kembali ke titik masuk utama SEOH.",
    },
    suggestionsEyebrow: "Lanjutkan",
    suggestionsTitle: "Gunakan rute yang stabil sementara ini diperiksa.",
    suggestions: [
      { label: "Beranda", description: "Mulai lagi dari halaman utama SEOH." },
      { label: "Layanan", description: "Jelajahi halaman layanan inti." },
      {
        label: "Kontak",
        description: "Kirim konteks jika masalah terus terjadi.",
      },
    ],
    retryLabel: "Coba lagi",
    digestLabel: "Referensi",
    statsAria: "Status halaman kesalahan",
    statusLabel: "Status",
    indexingLabel: "Pengindeksan",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Tidak diiklankan",
    skipLabel: "Lewati ke konten utama",
  },
  "zh-cn": {
    title: "部分内容未能正确加载。",
    lead: "页面遇到临时错误。您可以重试或从稳定的 SEOH 路由继续访问。",
    metadata: {
      title: "加载页面出错 | SEOH",
      description: "SEOH 无法加载此页面。请重试或从稳定路由继续。",
    },
    primaryAction: { label: "重试", description: "重新加载此部分。" },
    secondaryAction: { label: "前往首页", description: "返回 SEOH 主入口。" },
    suggestionsEyebrow: "继续浏览",
    suggestionsTitle: "在问题排查期间，请使用稳定路由。",
    suggestions: [
      { label: "首页", description: "从 SEOH 主页重新开始。" },
      { label: "服务", description: "浏览核心服务页面。" },
      { label: "联系我们", description: "如果问题持续发生，请发送详细信息。" },
    ],
    retryLabel: "重试",
    digestLabel: "参考",
    statsAria: "错误页面状态",
    statusLabel: "状态",
    indexingLabel: "索引状态",
    noindexValue: "不索引",
    ampNotAdvertisedValue: "未投放广告",
    skipLabel: "跳转到主要内容",
  },
  tr: {
    title: "Bir şey doğru yüklenemedi.",
    lead: "Sayfa geçici bir hatayla karşılaştı. Tekrar deneyebilir veya stabil bir SEOH rotasından devam edebilirsiniz.",
    metadata: {
      title: "Sayfa yüklenirken hata | SEOH",
      description:
        "SEOH bu sayfayı yükleyemedi. Tekrar deneyin veya stabil bir rotadan devam edin.",
    },
    primaryAction: {
      label: "Tekrar deneyin",
      description: "Bu bölümü yeniden yüklemesini isteyin.",
    },
    secondaryAction: {
      label: "Ana sayfaya git",
      description: "Ana SEOH giriş noktasına dönün.",
    },
    suggestionsEyebrow: "Devam edin",
    suggestionsTitle: "Kontrol edilirken stabil bir rota kullanın.",
    suggestions: [
      {
        label: "Ana sayfa",
        description: "Ana SEOH sayfasından tekrar başlayın.",
      },
      {
        label: "Hizmetler",
        description: "Temel hizmet sayfalarını inceleyin.",
      },
      {
        label: "İletişim",
        description: "Sorun devam ederse bağlamı gönderin.",
      },
    ],
    retryLabel: "Tekrar dene",
    digestLabel: "Referans",
    statsAria: "Hata sayfası durumu",
    statusLabel: "Durum",
    indexingLabel: "İndexleme",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Reklam verilmiyor",
    skipLabel: "Ana içeriğe atla",
  },
  ko: {
    title: "일부 항목이 올바르게 로드되지 않았습니다.",
    lead: "페이지에서 일시적 오류가 발생했습니다. 다시 시도하거나 안정적인 SEOH 경로로 이동하세요.",
    metadata: {
      title: "페이지 로드 오류 | SEOH",
      description:
        "SEOH가 이 페이지를 로드할 수 없습니다. 다시 시도하거나 안정적인 경로로 이동하세요.",
    },
    primaryAction: {
      label: "다시 시도",
      description: "이 섹션을 다시 불러오도록 요청합니다.",
    },
    secondaryAction: {
      label: "홈페이지로 이동",
      description: "SEOH 메인 진입점으로 돌아갑니다.",
    },
    suggestionsEyebrow: "계속 이동",
    suggestionsTitle: "문제가 해결되는 동안 안정적인 경로를 이용하세요.",
    suggestions: [
      {
        label: "홈페이지",
        description: "SEOH 메인 페이지에서 다시 시작하세요.",
      },
      { label: "서비스", description: "핵심 서비스 페이지를 확인하세요." },
      { label: "문의", description: "문제가 계속되면 상황을 전송하세요." },
    ],
    retryLabel: "다시 시도",
    digestLabel: "참고",
    statsAria: "오류 페이지 상태",
    statusLabel: "상태",
    indexingLabel: "인덱싱",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "광고되지 않음",
    skipLabel: "본문으로 건너뛰기",
  },
  it: {
    title: "Qualcosa non si è caricato correttamente.",
    lead: "La pagina ha riscontrato un errore temporaneo. Puoi riprovare o proseguire da un percorso stabile di SEOH.",
    metadata: {
      title: "Errore caricamento pagina | SEOH",
      description:
        "SEOH non è riuscita a caricare questa pagina. Riprova o continua da un percorso stabile.",
    },
    primaryAction: {
      label: "Riprova",
      description: "Richiedi il ricaricamento di questa sezione.",
    },
    secondaryAction: {
      label: "Vai alla homepage",
      description: "Torna al punto d'ingresso principale di SEOH.",
    },
    suggestionsEyebrow: "Continua",
    suggestionsTitle: "Usa un percorso stabile mentre verifichiamo.",
    suggestions: [
      {
        label: "Pagina iniziale",
        description: "Riparti dalla pagina principale di SEOH.",
      },
      {
        label: "Servizi",
        description: "Sfoglia le pagine dei servizi principali.",
      },
      {
        label: "Contatto",
        description: "Invia il contesto se il problema persiste.",
      },
    ],
    retryLabel: "Riprova",
    digestLabel: "Riferimento",
    statsAria: "Stato della pagina di errore",
    statusLabel: "Stato",
    indexingLabel: "Indicizzazione",
    noindexValue: "Noindex",
    ampNotAdvertisedValue: "Non pubblicizzato",
    skipLabel: "Vai al contenuto principale",
  },
};

function resolveLocale(locale: string | undefined): AppLocale {
  return ALL_APP_LOCALES.includes(locale as AppLocale)
    ? (locale as AppLocale)
    : DEFAULT_LOCALE;
}

function getMinimalLocalizedContent(
  locale: Exclude<AppLocale, "en">,
  copy: MinimalRuntimeErrorCopy,
): ErrorPageContent {
  const [home, services, contact] = copy.suggestions;

  return {
    statusCode: 500,
    eyebrow: "500",
    title: copy.title,
    lead: copy.lead,
    metadata: copy.metadata,
    primaryAction: { ...copy.primaryAction, href: "#retry" },
    secondaryAction: { ...copy.secondaryAction, href: `/${locale}` },
    suggestionsEyebrow: copy.suggestionsEyebrow,
    suggestionsTitle: copy.suggestionsTitle,
    suggestions: [
      { ...home, href: `/${locale}` },
      { ...services, href: `/${locale}/services` },
      { ...contact, href: `/${locale}/contact` },
    ],
    retryLabel: copy.retryLabel,
    digestLabel: copy.digestLabel,
    statsAria: copy.statsAria,
    statusLabel: copy.statusLabel,
    indexingLabel: copy.indexingLabel,
    noindexValue: copy.noindexValue,
    ampLabel: "AMP",
    ampNotAdvertisedValue: copy.ampNotAdvertisedValue,
  };
}

export type SynchronousRuntimeErrorContent = {
  readonly content: ErrorPageContent;
  readonly skipLabel: string;
};

/**
 * Return the safe initial boundary state without awaiting locale dictionaries.
 * Known non-English locales never borrow English wording or an `/en` route.
 */
export function getSynchronousRuntimeErrorContent(
  locale: string | undefined,
): SynchronousRuntimeErrorContent {
  const resolvedLocale = resolveLocale(locale);

  if (resolvedLocale === "en") {
    return {
      content: GLOBAL_RUNTIME_ERROR_FALLBACK,
      skipLabel: GLOBAL_RUNTIME_ERROR_SKIP_LABEL,
    };
  }

  const copy = MINIMAL_RUNTIME_ERROR_COPY[resolvedLocale];
  return {
    content: getMinimalLocalizedContent(resolvedLocale, copy),
    skipLabel: copy.skipLabel,
  };
}
